import React, { FC, ReactNode, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar, NavItem } from './Sidebar';
import { MobileNav } from './MobileNav';
import { AidFoneAssistant } from '../landing/AidFoneAssistant';
import { FEATURES } from '../../constants/featureFlags';
import { useAuth } from '../../context/AuthContext';

// Default navigation items
export const defaultMainMenuItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', path: '/dashboard' },
  ...(FEATURES.B2B_MULTI_SENIOR
    ? [{ id: 'manage', label: 'Manage Seniors', icon: 'group', path: '/manage-seniors' }]
    : []),
];

export const defaultSettingsMenuItems: NavItem[] = [
  { id: 'account', label: 'My Account', icon: 'manage_accounts', path: '/account' },
  { id: 'help', label: 'Help & Support', icon: 'help', path: '/help' },
];

interface DashboardLayoutProps {
  children: ReactNode;
  mainMenuItems?: NavItem[];
  settingsMenuItems?: NavItem[];
  currentUser?: {
    name: string;
    avatar?: string;
  };
  onLogout?: () => void;
  /** Allow sidebar collapse on desktop */
  collapsible?: boolean;
  /**
   * In B2C mode, the senior's name is injected as a dynamic nav entry
   * labeled "[Senior Name]'s Phone". Clicking it fires onSeniorNavClick.
   */
  seniorName?: string;
  onSeniorNavClick?: () => void;
}

export const DashboardLayout: FC<DashboardLayoutProps> = ({
  children,
  mainMenuItems,
  settingsMenuItems,
  currentUser,
  onLogout,
  collapsible = true,
  seniorName,
  onSeniorNavClick,
}) => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();

  // Translate the default menus on each render so language switches re-label items.
  const resolvedMainMenu: NavItem[] = mainMenuItems ?? [
    { id: 'dashboard', label: t('navigation.dashboard'), icon: 'dashboard', path: '/dashboard' },
    ...(FEATURES.B2B_MULTI_SENIOR
      ? [{ id: 'manage', label: t('navigation.manageSeniors'), icon: 'group', path: '/manage-seniors' }]
      : []),
  ];
  const resolvedSettingsMenu: NavItem[] = settingsMenuItems ?? [
    { id: 'account', label: t('navigation.myAccount'), icon: 'manage_accounts', path: '/account' },
    { id: 'help', label: t('navigation.helpSupport'), icon: 'help', path: '/help' },
  ];

  // Fall back to auth context when the parent page doesn't pass currentUser/onLogout,
  // so My Account / Help & Support / etc. get the real caregiver name automatically.
  const resolvedUser = currentUser ?? {
    name:
      (user?.user_metadata as { full_name?: string } | undefined)?.full_name ||
      user?.email?.split('@')[0] ||
      'Caregiver',
  };
  const resolvedLogout = onLogout ?? (() => signOut());

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Persist collapsed state
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    }
    return false;
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Inject a dynamic "[Senior Name]'s Phone" entry in B2C mode.
  // The path starts with '#' so the Sidebar treats it as a click handler, not a route.
  const mainMenuWithSenior = React.useMemo<NavItem[]>(() => {
    if (FEATURES.B2B_MULTI_SENIOR) return resolvedMainMenu;
    if (!seniorName) return resolvedMainMenu;
    return [
      ...resolvedMainMenu,
      {
        id: 'senior-phone',
        label: t('sidebarUi.seniorPhone', { name: seniorName }),
        icon: 'smartphone',
        path: '#senior-phone',
      },
    ];
  }, [resolvedMainMenu, seniorName, t]);

  const handleNavItemClick = (itemId: string) => {
    if (itemId === 'senior-phone') {
      onSeniorNavClick?.();
    }
  };

  return (
    <div className="flex h-screen h-[100dvh] w-full bg-brand-bg dark:bg-background-dark text-brand-text dark:text-gray-100 overflow-hidden">
      {/* Mobile Navigation */}
      <MobileNav
        mainMenuItems={mainMenuWithSenior}
        settingsMenuItems={resolvedSettingsMenu}
        currentUser={resolvedUser}
        onLogout={resolvedLogout}
      />

      {/* Desktop Sidebar */}
      <Sidebar
        mainMenuItems={mainMenuWithSenior}
        settingsMenuItems={resolvedSettingsMenu}
        currentUser={resolvedUser}
        onLogout={resolvedLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={collapsible ? () => setSidebarCollapsed(!sidebarCollapsed) : undefined}
        onItemClick={handleNavItemClick}
      />

      {/* Main Content */}
      <main
        className="flex-1 overflow-y-auto h-full relative bg-brand-bg dark:bg-slate-900 pt-16 md:pt-0"
        style={{ scrollbarGutter: 'stable' }}
      >
        <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-10 flex flex-col gap-6 lg:gap-10">
          {children}
        </div>
      </main>

      <AidFoneAssistant visitorType="customer" />
    </div>
  );
};

export default DashboardLayout;
